import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShowcaseItem as ShowcaseItemType, ShowcasePage } from '../../../types';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { updateItem, removeItem, addItem, updatePage, createItemAsync, updateItemAsync } from '../../../store/slices/showcaseSlice';
import { 
  TrashIcon, 
  ArrowsPointingOutIcon, 
  ArrowPathIcon,
  CheckIcon,
  XMarkIcon,
  Squares2X2Icon,
  CursorArrowRaysIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import ShowcaseItem from './ShowcaseItem';
import ConfirmationModal from '../../common/ConfirmationModal';
import { useRestrictions } from '../../../hooks/useRestrictions';
// [Subscription removed] UpgradePopup removed — portfolio items are unlimited.
// import UpgradePopup from '../../common/UpgradePopup';
import { compressImage } from '../../../utils/compression';

interface ShowcaseEditorProps {
  sectionId: string;
  page: ShowcasePage;
  onClose: () => void;
}

const ShowcaseEditor: React.FC<ShowcaseEditorProps> = ({ sectionId, page, onClose }) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const selectedItem = page.items.find(i => i.id === selectedItemId);

  const [resizingItemId, setResizingItemId] = useState<string | null>(null);
  const [tempSize, setTempSize] = useState<{ w: number, h: number } | null>(null);
  const [draggingItemId, setDraggingItemId] = useState<string | null>(null);
  const [tempPos, setTempPos] = useState<{ x: number, y: number } | null>(null);

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Modal States
  const [itemToRemove, setItemToRemove] = useState<string | null>(null);
  const [layoutToSwitch, setLayoutToSwitch] = useState<'grid' | 'canvas' | null>(null);

  // Local state for performance-sensitive sliders
  const [localRotation, setLocalRotation] = useState<number>(0);
  const [localBorderRadius, setLocalBorderRadius] = useState<number>(16);

  const {
    // checkRestriction,
    limits: tierLimits } = useRestrictions();
  // const [upgradeConfig, setUpgradeConfig] = useState<{
  //   isOpen: boolean;
  //   featureName?: string;
  //   limit?: string;
  //   tier?: 'pro' | 'premium';
  // }>({ isOpen: false });

  useEffect(() => {
    if (selectedItem) {
      setLocalRotation(selectedItem.rotation || 0);
      setLocalBorderRadius(selectedItem.border_radius !== undefined ? selectedItem.border_radius : 16);
    }
  }, [selectedItemId]);

  const handleUpdateItem = (updatedItem: ShowcaseItemType) => {
    dispatch(updateItemAsync({ sectionId, pageId: page.id, item: updatedItem }));
  };

  const handlePropertyChange = (property: keyof ShowcaseItemType, value: any) => {
    if (!selectedItem) return;
    const updatedItem = { ...selectedItem, [property]: value };
    handleUpdateItem(updatedItem);
  };

  const handleBringToFront = () => {
    if (!selectedItem) return;
    const maxOrder = Math.max(...page.items.map(i => i.order || 0), 0);
    handleUpdateItem({ ...selectedItem, order: maxOrder + 1 });
  };

  const handleSendToBack = () => {
    if (!selectedItem) return;
    const minOrder = Math.min(...page.items.map(i => i.order || 0), 0);
    handleUpdateItem({ ...selectedItem, order: minOrder - 1 });
  };

  const handleRemoveItem = (itemId: string) => {
    dispatch(removeItem({ sectionId, pageId: page.id, itemId }));
    setSelectedItemId(null);
    setItemToRemove(null);
  };

  const handleAddItem = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const allFiles = Array.from(e.target.files || []);
    if (allFiles.length === 0) return;

    const files = allFiles.filter(file => {
      if (file.size > 50 * 1024 * 1024) {
        toast.error(`File ${file.name} is too large. Maximum size is 50MB.`);
        return false;
      }
      return true;
    });

    // [Subscription removed] Portfolio item creation is unrestricted — platform is fully free.
    // const check = checkRestriction('create_portfolio_item', files.length);
    // if (!check.allowed) {
    //   setUpgradeConfig({
    //     isOpen: true,
    //     featureName: check.featureName,
    //     limit: check.limit,
    //     tier: check.tier,
    //   });
    //   return;
    // }

    try {
      setIsUploading(true);
      const { uploadAPI } = await import('../../../services/api');
      
      const results = await Promise.allSettled(
        files.map(async (file, index) => {
          try {
            const compressedFile = await compressImage(file);
            const formData = new FormData();
            formData.append('image', compressedFile);
            
            const response = await uploadAPI.uploadImage(formData);
            
            if (response.data?.success) {
              const imageUrl = response.data.data.url;
              const urls = response.data.data.urls || {};
              const newItem: ShowcaseItemType = {
                id: `new-${Date.now()}-${index}`,
                image_url: imageUrl,
                medium_url: urls.medium,
                thumbnail_url: urls.thumbnail,
                x: (index * 2) % 12,
                y: Math.floor((index * 2) / 12) * 2,
                w: page.layout_type === 'grid' ? 4 : 200,
                h: page.layout_type === 'grid' ? 4 : 200,
                border_type: 'solid',
                intensity: 50,
                border_radius: 16,
                direction: 'bottom',
                order: page.items.length + index
              };
              return { success: true, item: newItem, fileName: file.name };
            } else {
              return { success: false, error: response.data?.error || 'Upload failed', fileName: file.name };
            }
          } catch (error: any) {
            const errorMessage = error.response?.data?.error || error.message || 'Upload failed';
            return { success: false, error: errorMessage, fileName: file.name };
          }
        })
      );

      const successfulUploads: ShowcaseItemType[] = [];
      const failedUploads: { fileName: string; error: string }[] = [];

      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value.success && result.value.item) {
          successfulUploads.push(result.value.item);
        } else if (result.status === 'fulfilled' && !result.value.success) {
          failedUploads.push({ fileName: result.value.fileName, error: result.value.error });
        } else if (result.status === 'rejected') {
          failedUploads.push({ fileName: 'Unknown file', error: result.reason?.message || 'Upload failed' });
        }
      });

      // Add successful uploads to the showcase
      for (const item of successfulUploads) {
        await dispatch(createItemAsync({ sectionId, pageId: page.id, item }));
      }

      // Show appropriate feedback
      if (successfulUploads.length > 0) {
        setSelectedItemId(successfulUploads[successfulUploads.length - 1].id);
        toast.success(`Successfully uploaded ${successfulUploads.length} image(s)`);
      }

      if (failedUploads.length > 0) {
        failedUploads.forEach(({ fileName, error }) => {
          toast.error(`${fileName}: ${error}`, { duration: 5000 });
        });
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload images');
    } finally {
      setIsUploading(false);
      if (e.target) e.target.value = '';
    }
  };

  const toggleLayoutMode = (mode: 'grid' | 'canvas') => {
    dispatch(updatePage({ sectionId, page: { id: page.id, layout_type: mode } }));
    setLayoutToSwitch(null);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md flex flex-col">
      {/* Editor Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="bg-white/10 p-2 rounded-xl">
              {page.layout_type === 'grid' ? (
                <Squares2X2Icon className="h-6 w-6 text-white" />
              ) : (
                <CursorArrowRaysIcon className="h-6 w-6 text-white" />
              )}
            </div>
            <div>
              <h2 className="text-white font-bold">{page.title}</h2>
              <p className="text-white/50 text-[10px] uppercase tracking-widest">Layout Editor</p>
            </div>
          </div>

          <div className="h-8 w-px bg-white/10" />

          <div className="flex bg-white/5 p-1 rounded-xl border border-white/10">
            <button 
              onClick={() => setLayoutToSwitch('grid')}
              className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
                page.layout_type === 'grid' ? 'bg-white text-black' : 'text-white/50 hover:text-white'
              }`}
            >
              <Squares2X2Icon className="h-4 w-4" />
              Grid
            </button>
            <button 
              onClick={() => setLayoutToSwitch('canvas')}
              className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
                page.layout_type === 'canvas' ? 'bg-white text-black' : 'text-white/50 hover:text-white'
              }`}
            >
              <CursorArrowRaysIcon className="h-4 w-4" />
              Canvas
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <input 
            type="file" 
            ref={fileInputRef} 
            onChange={handleFileChange} 
            accept="image/*" 
            multiple
            className="hidden" 
          />
          <button 
            onClick={handleAddItem}
            disabled={isUploading}
            className="px-4 py-2.5 bg-white/10 text-white rounded-xl font-bold text-sm hover:bg-white/20 transition-all flex items-center gap-2 border border-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? (
              <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <PlusIcon className="h-4 w-4" />
            )}
            {isUploading ? 'Uploading...' : 'Add Image'}
          </button>
          <button 
            onClick={onClose}
            className="px-6 py-2.5 bg-teal-500 text-white rounded-xl font-bold text-sm hover:bg-teal-600 transition-all flex items-center gap-2 shadow-lg shadow-teal-500/20"
          >
            <CheckIcon className="h-4 w-4" />
            Done Editing
          </button>
          <button 
            onClick={onClose}
            className="p-2.5 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-all border border-white/10"
            title="Close Editor"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Main Canvas Area */}
        <div className="flex-1 overflow-auto p-8 flex justify-center items-start bg-[radial-gradient(#ffffff10_1px,transparent_1px)] [background-size:24px_24px]">
          <div 
            ref={containerRef}
            id="editor-canvas"
            className={`relative bg-white/5 rounded-3xl border-2 border-dashed border-white/20 min-h-[600px] w-full max-w-5xl shadow-2xl ${
              page.layout_type === 'grid' ? 'grid grid-cols-12 gap-4 p-6' : 'p-6'
            }`}
            style={page.layout_type === 'grid' ? {
              gridTemplateRows: `repeat(24, auto)`,
            } : {}}
          >
            {/* Grid Overlay */}
            {page.layout_type === 'grid' && (
              <div className="absolute inset-0 p-6 grid grid-cols-12 gap-4 pointer-events-none opacity-20">
                {Array.from({ length: 12 * 24 }).map((_, i) => (
                  <div key={i} className="border border-white/10 rounded-lg h-10" />
                ))}
              </div>
            )}

            {page.items.slice().sort((a, b) => (a.order || 0) - (b.order || 0)).map((item) => {
              const isResizing = resizingItemId === item.id;
              const isDragging = draggingItemId === item.id;
              
              const currentW = isResizing && tempSize ? tempSize.w : item.w;
              const currentH = isResizing && tempSize ? tempSize.h : item.h;
              const currentX = isDragging && tempPos ? tempPos.x : item.x;
              const currentY = isDragging && tempPos ? tempPos.y : item.y;

              const isSelected = selectedItemId === item.id;
              
              const displayItem = {
                ...item,
                w: currentW,
                h: currentH,
                x: currentX,
                y: currentY,
                rotation: isSelected ? localRotation : item.rotation,
                border_radius: isSelected ? localBorderRadius : item.border_radius
              };

              return (
                <ShowcaseItem
                  key={item.id}
                  item={displayItem}
                  isOwnProfile={true}
                  layoutType={page.layout_type}
                  isEditing={true}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setSelectedItemId(item.id);
                    
                    const startX = e.clientX;
                    const startY = e.clientY;
                    const initialX = item.x;
                    const initialY = item.y;

                    setDraggingItemId(item.id);

                    const onMouseMove = (moveEvent: MouseEvent) => {
                      const deltaX = moveEvent.clientX - startX;
                      const deltaY = moveEvent.clientY - startY;

                      if (page.layout_type === 'grid' && containerRef.current) {
                        const gridWidth = containerRef.current.offsetWidth - 48;
                        const cellWidth = gridWidth / 12;
                        const cellHeight = 56;
                        const newX = Math.max(0, Math.min(11, Math.round((initialX * cellWidth + deltaX) / cellWidth)));
                        const newY = Math.max(0, Math.round((initialY * cellHeight + deltaY) / cellHeight));
                        setTempPos({ x: newX, y: newY });
                      } else {
                        setTempPos({ x: initialX + deltaX, y: initialY + deltaY });
                      }
                    };

                    const onMouseUp = (upEvent: MouseEvent) => {
                      document.removeEventListener('mousemove', onMouseMove);
                      document.removeEventListener('mouseup', onMouseUp);
                      
                      const deltaX = upEvent.clientX - startX;
                      const deltaY = upEvent.clientY - startY;
                      
                      let finalX = initialX;
                      let finalY = initialY;

                      if (page.layout_type === 'grid' && containerRef.current) {
                        const gridWidth = containerRef.current.offsetWidth - 48;
                        const cellWidth = gridWidth / 12;
                        const cellHeight = 56;
                        finalX = Math.max(0, Math.min(11, Math.round((initialX * cellWidth + deltaX) / cellWidth)));
                        finalY = Math.max(0, Math.round((initialY * cellHeight + deltaY) / cellHeight));
                      } else {
                        finalX = initialX + deltaX;
                        finalY = initialY + deltaY;
                      }
                      
                      handleUpdateItem({...item, x: finalX, y: finalY});
                      setDraggingItemId(null);
                      setTempPos(null);
                    };

                    document.addEventListener('mousemove', onMouseMove);
                    document.addEventListener('mouseup', onMouseUp);
                  }}
                  className={`cursor-move transition-all duration-300 ${
                    selectedItemId === item.id ? 'ring-4 ring-teal-500 ring-offset-4 ring-offset-black/50 z-50 shadow-2xl scale-[1.02]' : 'hover:ring-2 hover:ring-white/30'
                  } ${isDragging ? 'opacity-50 z-40' : ''}`}
                >
                  {selectedItemId === item.id && (
                    <>
                      <div className="absolute -top-3 -right-3 flex gap-2 z-[60]">
                        <button 
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            setItemToRemove(item.id); 
                          }}
                          className="bg-red-500 text-white p-2 rounded-full shadow-lg hover:bg-red-600 transition-all hover:scale-110 active:scale-90"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Rotation Handle */}
                      {page.layout_type === 'canvas' && (
                        <div 
                          className="absolute -top-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 cursor-alias z-[200]"
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            const rect = containerRef.current?.getBoundingClientRect();
                            if (!rect) return;
                            
                            // Calculate center of the item relative to the viewport
                            const itemElement = e.currentTarget.parentElement;
                            const itemRect = itemElement?.getBoundingClientRect();
                            if (!itemRect) return;
                            
                            const centerX = itemRect.left + itemRect.width / 2;
                            const centerY = itemRect.top + itemRect.height / 2;

                            const onMouseMove = (moveEvent: MouseEvent) => {
                              const angle = Math.atan2(moveEvent.clientY - centerY, moveEvent.clientX - centerX);
                              // Add 90 degrees because the handle is at the top
                              let degree = (angle * 180 / Math.PI) + 90;
                              setLocalRotation(Math.round(degree));
                            };

                            const onMouseUp = () => {
                              document.removeEventListener('mousemove', onMouseMove);
                              document.removeEventListener('mouseup', onMouseUp);
                              handleUpdateItem({ ...item, rotation: localRotation });
                            };

                            document.addEventListener('mousemove', onMouseMove);
                            document.addEventListener('mouseup', onMouseUp);
                          }}
                        >
                          <div className="w-0.5 h-6 bg-teal-500" />
                          <div className="bg-teal-500 p-1.5 rounded-full shadow-lg hover:scale-110 transition-transform">
                            <ArrowPathIcon className="h-3 w-3 text-white" />
                          </div>
                        </div>
                      )}

                      {/* Resize Handle (Bottom Right) */}
                      <div 
                        className="absolute bottom-0 right-0 w-8 h-8 bg-teal-500 rounded-tl-xl cursor-nwse-resize flex items-center justify-center z-20 shadow-lg hover:scale-110 transition-transform"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          const startX = e.clientX;
                          const startY = e.clientY;
                          const startW = item.w;
                          const startH = item.h;
                          const aspectRatio = startW / startH;
                          
                          setResizingItemId(item.id);
                          setTempSize({ w: startW, h: startH });

                          const onMouseMove = (moveEvent: MouseEvent) => {
                            const deltaX = moveEvent.clientX - startX;
                            const deltaY = moveEvent.clientY - startY;
                            
                            let newW, newH;

                            if (page.layout_type === 'grid' && containerRef.current) {
                              const gridWidth = containerRef.current.offsetWidth - 48;
                              const cellWidth = gridWidth / 12;
                              const cellHeight = 56;
                              
                              newW = Math.max(1, Math.min(12 - item.x, Math.round((startW * cellWidth + deltaX) / cellWidth)));
                              newH = Math.round(newW / aspectRatio);
                              setTempSize({ w: newW, h: Math.max(1, newH) });
                            } else {
                              newW = Math.max(50, startW + deltaX);
                              newH = newW / aspectRatio;
                              setTempSize({ w: newW, h: newH });
                            }
                          };

                          const onMouseUp = (upEvent: MouseEvent) => {
                            document.removeEventListener('mousemove', onMouseMove);
                            document.removeEventListener('mouseup', onMouseUp);
                            
                            const deltaX = upEvent.clientX - startX;
                            let finalW, finalH;

                            if (page.layout_type === 'grid' && containerRef.current) {
                              const gridWidth = containerRef.current.offsetWidth - 48;
                              const cellWidth = gridWidth / 12;
                              finalW = Math.max(1, Math.min(12 - item.x, Math.round((startW * cellWidth + deltaX) / cellWidth)));
                              finalH = Math.max(1, Math.round(finalW / aspectRatio));
                            } else {
                              finalW = Math.max(50, startW + deltaX);
                              finalH = finalW / aspectRatio;
                            }

                            handleUpdateItem({...item, w: finalW, h: finalH});
                            setResizingItemId(null);
                            setTempSize(null);
                          };

                          document.addEventListener('mousemove', onMouseMove);
                          document.addEventListener('mouseup', onMouseUp);
                        }}
                      >
                        <ArrowsPointingOutIcon className="h-4 w-4 text-white" />
                      </div>
                    </>
                  )}
                </ShowcaseItem>
              );
            })}
          </div>
        </div>

        {/* Properties Sidebar */}
        <AnimatePresence>
          {selectedItem && (
            <motion.div
              initial={{ x: 300, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 300, opacity: 0 }}
              className="w-80 bg-black/40 border-l border-white/10 p-6 overflow-y-auto backdrop-blur-2xl"
            >
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="text-white font-bold">Item Properties</h3>
                  <p className="text-white/30 text-[10px] uppercase tracking-widest mt-1">Refine your layout</p>
                </div>
                <button 
                  onClick={() => setSelectedItemId(null)}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-white/50 hover:text-white" />
                </button>
              </div>

              <div className="space-y-8">
                {/* Delete Item (Prominent) */}
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl">
                  <h4 className="text-red-500 text-[10px] uppercase font-bold tracking-widest mb-3">Danger Zone</h4>
                  <button 
                    onClick={() => setItemToRemove(selectedItem.id)}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-red-500 text-white rounded-xl font-bold text-xs hover:bg-red-600 transition-all active:scale-95 shadow-lg shadow-red-500/20"
                  >
                    <TrashIcon className="h-4 w-4" />
                    Delete This Item
                  </button>
                </div>

                {/* Position & Size */}
                <div className="space-y-4">
                  <h4 className="text-white/30 text-[10px] uppercase font-bold tracking-widest">Layout (Drag labels to scrub)</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { label: 'X Position', key: 'x', step: 5 },
                      { label: 'Y Position', key: 'y', step: 5 },
                      { label: 'Width', key: 'w', step: 2, isSize: true },
                      { label: 'Height', key: 'h', step: 2, isSize: true },
                    ].map((field) => (
                      <div key={field.key} className="space-y-1.5">
                        <label 
                          className="text-white/50 text-[10px] cursor-ew-resize hover:text-teal-500 transition-colors select-none flex items-center gap-1"
                          onMouseDown={(e) => {
                            const startX = e.clientX;
                            const initialW = selectedItem.w;
                            const initialH = selectedItem.h;
                            const initialX = selectedItem.x;
                            const initialY = selectedItem.y;
                            const ratio = initialW / initialH;
                            
                            const onMouseMove = (mE: MouseEvent) => {
                              const delta = Math.round((mE.clientX - startX) / field.step);
                              if (field.isSize) {
                                if (field.key === 'w') {
                                  const newW = Math.max(10, initialW + delta);
                                  handleUpdateItem({...selectedItem, w: newW, h: Math.round(newW / ratio)});
                                } else {
                                  const newH = Math.max(10, initialH + delta);
                                  handleUpdateItem({...selectedItem, h: newH, w: Math.round(newH * ratio)});
                                }
                              } else {
                                handleUpdateItem({...selectedItem, [field.key]: (field.key === 'x' ? initialX : initialY) + delta});
                              }
                            };
                            const onMouseUp = () => {
                              document.removeEventListener('mousemove', onMouseMove);
                              document.removeEventListener('mouseup', onMouseUp);
                            };
                            document.addEventListener('mousemove', onMouseMove);
                            document.addEventListener('mouseup', onMouseUp);
                          }}
                        >
                          {field.label} <ArrowPathIcon className="h-2.5 w-2.5 opacity-30" />
                        </label>
                        <input 
                          type="number" 
                          value={(selectedItem as any)[field.key]}
                          onChange={(e) => {
                            const val = parseInt(e.target.value) || 0;
                            if (field.isSize) {
                              const ratio = selectedItem.w / selectedItem.h;
                              if (field.key === 'w') {
                                handleUpdateItem({...selectedItem, w: val, h: Math.round(val / ratio)});
                              } else {
                                handleUpdateItem({...selectedItem, h: val, w: Math.round(val * ratio)});
                              }
                            } else {
                              handleUpdateItem({...selectedItem, [field.key]: val});
                            }
                          }}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:border-teal-500 outline-none transition-all focus:bg-white/10"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Style */}
                <div className="space-y-4">
                  <h4 className="text-white/30 text-[10px] uppercase font-bold tracking-widest">Style</h4>
                  <div className="space-y-3">
                    <label className="text-white/50 text-[10px]">Border Style</label>
                    <div className="grid grid-cols-2 gap-2">
                      {['none', 'solid', 'fading-soft', 'fading-blended'].map((style) => (
                        <button
                          key={style}
                          onClick={() => handleUpdateItem({...selectedItem, border_type: style as any})}
                          className={`px-3 py-2.5 rounded-xl text-[10px] font-bold transition-all border ${
                            selectedItem.border_type === style 
                              ? 'bg-teal-500 border-teal-500 text-white shadow-lg shadow-teal-500/20' 
                              : 'bg-white/5 border-white/10 text-white/50 hover:border-white/30 hover:bg-white/10'
                          }`}
                        >
                          {style.replace('-', ' ').toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Border Intensity / Thickness */}
                  {(selectedItem.border_type.startsWith('fading') || selectedItem.border_type === 'solid') && (
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <label className="text-white/50 text-[10px]">
                          {selectedItem.border_type === 'solid' ? 'Border Thickness / Opacity' : 'Effect Intensity'}
                        </label>
                        <span className="text-teal-500 text-[10px] font-bold">{selectedItem.intensity || 50}%</span>
                      </div>
                      <input 
                        type="range" 
                        min="0" 
                        max="100" 
                        value={selectedItem.intensity || 50}
                        onChange={(e) => handleUpdateItem({...selectedItem, intensity: parseInt(e.target.value)})}
                        className="w-full accent-teal-500 h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer"
                      />
                    </div>
                  )}

                  {/* Direction Control */}
                  {selectedItem.border_type.startsWith('fading') && (
                    <div className="space-y-3">
                      <label className="text-white/50 text-[10px]">Fade Direction</label>
                      <div className="grid grid-cols-4 gap-2">
                        {['top', 'bottom', 'left', 'right'].map((dir) => (
                          <button
                            key={dir}
                            onClick={() => handleUpdateItem({...selectedItem, direction: dir as any})}
                            className={`py-2 rounded-lg text-[10px] font-bold transition-all border ${
                              (selectedItem.direction || 'bottom') === dir 
                                ? 'bg-teal-500 border-teal-500 text-white' 
                                : 'bg-white/5 border-white/10 text-white/50 hover:border-white/30'
                            }`}
                          >
                            {dir.toUpperCase()}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Corner Radius */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <label className="text-white/50 text-[10px]">Corner Radius</label>
                      <span className="text-teal-500 text-[10px] font-bold">{localBorderRadius}px</span>
                    </div>
                    <input 
                      type="range" 
                      min="0" 
                      max="100" 
                      value={localBorderRadius}
                      onChange={(e) => setLocalBorderRadius(parseInt(e.target.value))}
                      onMouseUp={() => handleUpdateItem({...selectedItem, border_radius: localBorderRadius})}
                      className="w-full accent-teal-500 h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  {/* Rotation */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <label className="text-white/50 text-[10px]">Rotation</label>
                      <span className="text-teal-500 text-[10px] font-bold">{localRotation}°</span>
                    </div>
                    <input 
                      type="range" 
                      min="-180" 
                      max="180" 
                      value={localRotation}
                      onChange={(e) => setLocalRotation(parseInt(e.target.value))}
                      onMouseUp={() => handleUpdateItem({...selectedItem, rotation: localRotation})}
                      className="w-full accent-teal-500 h-1.5 bg-white/10 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                </div>

                {/* Z-Index */}
                <div className="space-y-4">
                  <h4 className="text-white/30 text-[10px] uppercase font-bold tracking-widest">Ordering</h4>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={handleSendToBack}
                      className="flex-1 bg-white/5 border border-white/10 rounded-xl py-3 text-white text-xs hover:bg-white/10 transition-all active:scale-95"
                    >
                      Send Back
                    </button>
                    <button 
                      onClick={handleBringToFront}
                      className="flex-1 bg-white/5 border border-white/10 rounded-xl py-3 text-white text-xs hover:bg-white/10 transition-all active:scale-95"
                    >
                      Bring Front
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Custom Modals */}
      <ConfirmationModal
        isOpen={!!itemToRemove}
        onClose={() => setItemToRemove(null)}
        onConfirm={() => itemToRemove && handleRemoveItem(itemToRemove)}
        title="Remove Item"
        message="Are you sure you want to remove this item from your showcase?"
        confirmText="Remove"
        type="danger"
      />

      <ConfirmationModal
        isOpen={!!layoutToSwitch}
        onClose={() => setLayoutToSwitch(null)}
        onConfirm={() => layoutToSwitch && toggleLayoutMode(layoutToSwitch)}
        title="Switch Layout Mode"
        message={`Switching to ${layoutToSwitch} mode will reset some layout positions. Continue?`}
        confirmText="Switch Mode"
        type="warning"
      />

      {/* [Subscription removed] UpgradePopup removed */}
      {/*<UpgradePopup */}
      {/*  isOpen={upgradeConfig.isOpen}*/}
      {/*  onClose={() => setUpgradeConfig({ ...upgradeConfig, isOpen: false })}*/}
      {/*  featureName={upgradeConfig.featureName}*/}
      {/*  limit={upgradeConfig.limit}*/}
      {/*  tier={upgradeConfig.tier}*/}
      {/*/>*/}
    </div>
  );
};

export default ShowcaseEditor;
